import { signal } from "@preact/signals";

import readlangClient from "readlangClient";
import * as utils from "shared/utils";

export interface ShelfBook {
  bookID: string;
}

export interface ShelfData {
  _id: string;
  name?: string;
  description?: string;
  books: ShelfBook[];
  bookCount?: number;
  public?: boolean;
  hide?: boolean;
  language?: string;
  deleted?: boolean;
}

// --- Pure helper functions ---

export const getValidBooks = (shelf: ShelfData): ShelfBook[] => {
  if (!shelf.books) {
    return [];
  }
  return shelf.books.filter((book) => book && book.bookID);
};

export const getBookIndex = (shelf: ShelfData): Record<string, ShelfBook> => {
  const index: Record<string, ShelfBook> = {};
  for (const book of getValidBooks(shelf)) {
    index[book.bookID] = book;
  }
  return index;
};

export const getPrivacyValue = (
  shelf: ShelfData
): "private" | "linkOnly" | "public" => {
  return !shelf.public ? "private" : shelf.hide ? "linkOnly" : "public";
};

export const shelfViewJSON = (shelf: ShelfData) => {
  const json = { ...shelf } as any;
  json.descriptionHtml = utils.simpleMarkupToHtml(json.description);

  const privacyValue = getPrivacyValue(shelf);
  json.privacyValues = {
    private: privacyValue === "private",
    linkOnly: privacyValue === "linkOnly",
    public: privacyValue === "public",
  };
  return json;
};

// --- Store class ---

export default class ShelvesStore {
  readonly shelves = signal<ShelfData[]>([]);
  readonly selectedShelfID = signal<string | null>(null);

  get unshelved(): ShelfData | undefined {
    return this.shelves.value.find((shelf) => shelf._id === "unshelved");
  }

  reset() {
    this.shelves.value = [];
    this.selectedShelfID.value = null;
  }

  async fetch({ language }: { language: string }) {
    const { data } = await readlangClient.request("GET", "/api/shelves", {
      language,
    });
    const shelvesData = data as ShelfData[];
    this.shelves.value = shelvesData;
  }

  get(shelfID: string): ShelfData | undefined {
    return this.shelves.value.find((shelf) => shelf._id === shelfID);
  }

  async createShelf(attrs: {
    name: string;
    language: string;
    books: ShelfBook[];
  }): Promise<ShelfData> {
    const { data } = await readlangClient.request("POST", "/api/shelf", attrs);
    const newShelf = data as ShelfData;
    this.shelves.value = [...this.shelves.value, newShelf];
    return newShelf;
  }

  async patchShelf(
    shelfID: string,
    patch: Partial<ShelfData>
  ): Promise<ShelfData> {
    const previousShelf = this.get(shelfID);
    const previousBookIDs = previousShelf
      ? getValidBooks(previousShelf).map((book) => book.bookID)
      : [];

    // Server returns { success: true }, not the updated shelf,
    // so we apply the patch locally
    await readlangClient.request("PATCH", "/api/shelf/" + shelfID, patch);

    const updatedShelf = previousShelf
      ? this.withUpdatedBookCount({ ...previousShelf, ...patch }, patch)
      : this.withUpdatedBookCount(
          { _id: shelfID, books: [], ...patch } as ShelfData,
          patch
        );

    this.shelves.value = this.shelves.value.map((shelf) =>
      shelf._id === shelfID ? updatedShelf : shelf
    );

    if (patch.books !== undefined) {
      const newBookIDs = getValidBooks(updatedShelf).map((book) => book.bookID);
      this.syncUnshelved(shelfID, previousBookIDs, newBookIDs);
    }

    return updatedShelf;
  }

  async destroyShelf(shelfID: string) {
    await readlangClient.request("DELETE", "/api/shelf/" + shelfID, {});
    this.shelves.value = this.shelves.value.filter(
      (shelf) => shelf._id !== shelfID
    );
  }

  updateShelfBooks(shelfID: string, books: ShelfBook[]) {
    this.shelves.value = this.shelves.value.map((shelf) =>
      shelf._id === shelfID
        ? this.withUpdatedBookCount({ ...shelf, books }, { books })
        : shelf
    );
  }

  shelvesContainingBooks(bookIDs: string[]): ShelfData[] {
    return this.shelves.value.filter(
      (shelf) =>
        shelf._id !== "unshelved" &&
        getValidBooks(shelf).some((book) => bookIDs.includes(book.bookID))
    );
  }

  addNewBook(bookID: string) {
    const shelves = this.shelvesContainingBooks([bookID]);
    if (shelves.length === 0 && this.unshelved) {
      const unshelved = this.unshelved;
      const existingBookIDs = getValidBooks(unshelved).map(
        (book) => book.bookID
      );
      if (!existingBookIDs.includes(bookID)) {
        this.updateShelfBooks("unshelved", [...unshelved.books, { bookID }]);
      }
    }
  }

  private syncUnshelved(
    shelfID: string,
    previousBookIDs: string[],
    newBookIDs: string[]
  ) {
    if (shelfID === "unshelved") {
      return;
    }

    const unshelved = this.unshelved;
    if (!unshelved) {
      return;
    }

    const booksAdded = newBookIDs.filter((id) => !previousBookIDs.includes(id));
    const booksRemoved = previousBookIDs.filter(
      (id) => !newBookIDs.includes(id)
    );

    let unshelvedBooks = [...unshelved.books];
    let changed = false;

    // When books added to a non-unshelved shelf: remove those bookIDs from unshelved
    if (booksAdded.length > 0) {
      const unshelvedBookIDs = getValidBooks(unshelved).map(
        (book) => book.bookID
      );
      const filteredIDs = unshelvedBookIDs.filter(
        (id) => !booksAdded.includes(id)
      );
      if (filteredIDs.length !== unshelvedBookIDs.length) {
        unshelvedBooks = filteredIDs.map((id) => ({ bookID: id }));
        changed = true;
      }
    }

    // When books removed from a non-unshelved shelf: add back to unshelved if not on any other shelf
    if (booksRemoved.length > 0) {
      for (const bookID of booksRemoved) {
        const onShelf = this.shelves.value.some(
          (innerShelf) =>
            innerShelf._id !== "unshelved" &&
            getValidBooks(innerShelf)
              .map((book) => book.bookID)
              .includes(bookID)
        );
        if (!onShelf) {
          unshelvedBooks.push({ bookID });
          changed = true;
        }
      }
    }

    if (changed) {
      this.updateShelfBooks("unshelved", unshelvedBooks);
    }
  }

  private withUpdatedBookCount(
    shelf: ShelfData,
    patch: Partial<ShelfData>
  ): ShelfData {
    if (patch.books === undefined || shelf.bookCount === undefined) {
      return shelf;
    }

    return {
      ...shelf,
      bookCount: getValidBooks(shelf).length,
    };
  }
}
